// engine/collision/CollisionSystem.ts
import Phaser from "phaser";
import { ColliderRegistry } from "../core/ColliderRegistry";
import { EntityManager } from "../entities/EntityManager";
import { EventManager } from "../events/EventManager";
import { WinConditionManager } from "../events/WinConditionManager";

/**
 * Définition déclarative des collisions/overlaps :
 * - a, b : noms de groupes (résolus vers des arrays de bodies)
 * - type : "collide" ou "overlap"
 * - onCollide : callback déclenchée quand la collision/overlap passe
 * - process : prédicat (true pour autoriser onCollide)
 */
export const PUSHABLE = {
    PUSH_VELOCITY: 300,   // impulsion max transmise
    MIN_CHAR_SPEED: 6,    // déclenche même à petite vitesse
    PUSH_SCALE: 1.0,      // transmet 100% de la vitesse du perso

    DRAG: 90,             // décélération (plus bas = plus de glisse)
    MAX_VELOCITY: 320,    // plafond cohérent avec PUSH_VELOCITY

    STOP_EPS: 2,          // seuil d'arrêt net
    MIN_TOUCH_PUSH: 80,
};

export const PICKABLE = {
    OVERLAP_RATIO: 0.5, // 50% d’overlap requis (par rapport à l’objet)
};

export type CollisionRule = {
    id?: string;
    a: string;
    b: string;
    type: "collide" | "overlap";
    onCollide?: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;
    process?: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;
    enabled?: boolean;
};

type BodiesProvider = () => Phaser.Types.Physics.Arcade.ArcadeColliderType[];

// ─────────────────────────── Helpers META & Type Guards ───────────────────────────
type TFlags = { pickable?: boolean; immovable?: boolean; pushable?: boolean; };

type CollideArg =
    | Phaser.Types.Physics.Arcade.GameObjectWithBody
    | Phaser.Physics.Arcade.Body
    | Phaser.Tilemaps.Tile;

/** Normalise un paramètre callback (Body|Tile|GO) en GameObject si possible. */
function asGO(x: CollideArg | null | undefined): Phaser.GameObjects.GameObject | null {
    if (!x) return null;

    const anyX = x as any;

    // Déjà un GameObject (Sprite/Image/Container…)
    if (anyX?.getData && anyX?.scene) return anyX as Phaser.GameObjects.GameObject;

    // Body → .gameObject
    if ((x as Phaser.Physics.Arcade.Body).gameObject) {
        return (x as Phaser.Physics.Arcade.Body).gameObject as Phaser.GameObjects.GameObject;
    }

    // Tile : pas de data exploitable ici → null
    return null;
}

/** Lecture des meta (id/kind/entity/flags) à partir d’un Body|Tile|GO. */
function getMetaFromAny(x: CollideArg): {
    id?: string;
    kind?: "character" | "object";
    entity?: any;
    flags: TFlags;
} {
    const go = asGO(x);
    if (!go) return { flags: {} };
    const get = (go as any).getData?.bind(go) ?? (() => undefined);
    return {
        id: get("id"),
        kind: get("kind"),
        entity: get("entity"),
        flags: (get("flags") as TFlags) || {},
    };
}

function getBodyRect(go: any) {
    const b = go?.body as Phaser.Physics.Arcade.Body | undefined;
    if (b) {
        return { x1: b.x, y1: b.y, x2: b.x + b.width, y2: b.y + b.height, area: b.width * b.height };
    }
    const r = go?.getBounds?.();
    if (r) return { x1: r.left, y1: r.top, x2: r.right, y2: r.bottom, area: r.width * r.height };
    return { x1: 0, y1: 0, x2: 0, y2: 0, area: 0 };
}

/** Ratio d’overlap = surface_intersection / surface_objet */
function overlapRatioCharOverObject(charGO: any, objGO: any): number {
    const A = getBodyRect(charGO);
    const B = getBodyRect(objGO);
    const w = Math.max(0, Math.min(A.x2, B.x2) - Math.max(A.x1, B.x1));
    const h = Math.max(0, Math.min(A.y2, B.y2) - Math.max(A.y1, B.y1));
    const inter = w * h;
    return B.area > 0 ? inter / B.area : 0;
}

const isCharAny = (x: CollideArg) => getMetaFromAny(x).kind === "character";
const isObjAny = (x: CollideArg) => getMetaFromAny(x).kind === "object";

// ─────────────────────────────────── System ───────────────────────────────────
export class CollisionSystem {
    private scene: Phaser.Scene;
    private colliders: ColliderRegistry;
    private entities: EntityManager;

    // Groupes connus → providers
    private providers = new Map<string, BodiesProvider>();

    // Règles déclaratives
    private rules: CollisionRule[] = [];

    // Colliders construits (pour teardown sélectif)
    private built: Phaser.Physics.Arcade.Collider[] = [];

    // Intégrations
    private events?: EventManager;
    private win?: WinConditionManager;

    // Paramètres
    private pushVelocity = PUSHABLE.PUSH_VELOCITY;
    private minCharSpeedToPush = PUSHABLE.MIN_CHAR_SPEED;
    private pushScale = PUSHABLE.PUSH_SCALE;
    private objectDrag = PUSHABLE.DRAG;
    private objectMaxVelocity = PUSHABLE.MAX_VELOCITY;

    constructor(
        scene: Phaser.Scene,
        colliders: ColliderRegistry,
        entities: EntityManager,
        opts?: {
            events?: EventManager; win?: WinConditionManager;
            pushVelocity?: number;          // plafond (px/s)
            minCharSpeedToPush?: number;    // plancher (px/s)
            pushScale?: number;             // % de la vitesse du perso transférée
            objectDrag?: number;            // drag de l’objet pendant le push
            objectMaxVelocity?: number;     // vitesse max objet
            debug?: boolean;
        }) {
        this.scene = scene;
        this.colliders = colliders;
        this.entities = entities;

        this.events = opts?.events;
        this.win = opts?.win;
        if (opts?.pushVelocity != null) this.pushVelocity = opts.pushVelocity;
        if (opts?.minCharSpeedToPush != null) this.minCharSpeedToPush = opts.minCharSpeedToPush;
        if (opts?.pushScale != null) this.pushScale = opts.pushScale;
        if (opts?.objectDrag != null) this.objectDrag = opts.objectDrag;
        if (opts?.objectMaxVelocity != null) this.objectMaxVelocity = opts.objectMaxVelocity;
        if (opts?.debug) this.colliders.setDebug(true);

        // Groupes par défaut
        this.registerGroup("characters", () => this.entities.getCharacterBodies());
        this.registerGroup("objects", () => this.entities.getObjectBodies());
        this.registerGroup("texts", () => this.entities.getTextBodies?.() ?? []);

        // Teardown scène
        scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
        scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());
    }

    // ──────────────── Process (filtres) SAFE (Body|Tile|GO) ────────────────
    private processPickable: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (_a, b) => {
        return !!getMetaFromAny(b as CollideArg).flags.pickable;
    };

    private processImmovable: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (_a, b) => {
        return !!getMetaFromAny(b as CollideArg).flags.immovable;
    };

    private processPushable: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (_a, b) => {
        return !!getMetaFromAny(b as CollideArg).flags.pushable;
    };

    // ───────────────────────── API groupes/règles ─────────────────────────
    /** Ajoute/remplace un groupe. Utile pour couches/zones custom. */
    registerGroup(name: string, provider: BodiesProvider) {
        this.providers.set(name, provider);
    }

    /** Retire un groupe custom. */
    unregisterGroup(name: string) {
        this.providers.delete(name);
    }

    /** Ajoute 1..n règles. (appelle `rebuild()` après modif règles/entités) */
    addRule(...rules: CollisionRule[]) {
        for (const r of rules) {
            if (r.id) {
                const i = this.rules.findIndex(x => x.id === r.id);
                if (i >= 0) { this.rules[i] = r; continue; }
            }
            this.rules.push(r);
        }
    }

    /** Active/Désactive une règle par id. */
    setRuleEnabled(id: string, enabled: boolean) {
        const r = this.rules.find(x => x.id === id);
        if (r) r.enabled = enabled;
    }

    /** Supprime une règle par id. */
    removeRule(id: string) {
        this.rules = this.rules.filter(r => r.id !== id);
    }

    /** Rebuild complet : détruit les colliders construits, puis reconstruit depuis les règles. */
    rebuild() {
        this.destroyBuiltOnly();

        for (const rule of this.rules) {
            if (rule.enabled === false) continue;
            const A = this.providers.get(rule.a)?.() ?? [];
            const B = this.providers.get(rule.b)?.() ?? [];
            if (!A.length || !B.length) continue;

            const collider =
                rule.type === "collide"
                    ? this.scene.physics.add.collider(A as any, B as any, rule.onCollide, rule.process, this.scene)
                    : this.scene.physics.add.overlap(A as any, B as any, rule.onCollide, rule.process, this.scene);

            if (collider) {
                this.built.push(collider);
                this.colliders.add(collider); // on garde aussi dans le registre global 
            }
        }
    }

    /** Détruit uniquement ce que ce système a construit. */
    private destroyBuiltOnly() {
        for (const c of this.built) { try { c.destroy(); } catch { /* noop */ } }
        this.built.length = 0;
    }

    /** Teardown complet (idempotent). */
    destroy() {
        this.destroyBuiltOnly();
    }

    // ─────────────────────────── Callbacks SAFE ───────────────────────────
    private onCharCharCollide: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (aArg, bArg) => {
        if (!isCharAny(aArg as CollideArg) || !isCharAny(bArg as CollideArg)) return;

        const goA = asGO(aArg as CollideArg), goB = asGO(bArg as CollideArg);
        if (!goA || !goB) return;

        const A = getMetaFromAny(aArg as CollideArg);
        const B = getMetaFromAny(bArg as CollideArg);

        const bodyA = (goA as any).body as Phaser.Physics.Arcade.Body | undefined;
        const bodyB = (goB as any).body as Phaser.Physics.Arcade.Body | undefined;
        bodyA?.setVelocity(0, 0);
        bodyB?.setVelocity(0, 0);

        A.entity?.playCollisionAnimation?.(B.entity);
        B.entity?.playCollisionAnimation?.(A.entity);

        this.colliders.log("Char↔Char", { A: A.id, B: B.id });
        this.events?.emit("collision:char-char", { aId: A.id, bId: B.id, a: A.entity, b: B.entity });
        this.win?.evaluateOnEvent?.({ type: "collision:char-char", aId: A.id, bId: B.id } as any);

        if (A.id && B.id) this.events?.dispatchCollisionEvent(A.id, B.id);
    };

    private onCharPickableOverlap: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (charArg, objArg) => {

        if (!isCharAny(charArg as CollideArg) || !isObjAny(objArg as CollideArg)) {
            return;
        }

        const charGO = asGO(charArg as CollideArg);
        const objGO = asGO(objArg as CollideArg);

        if (!charGO || !objGO) {
            return;
        }

        const C = getMetaFromAny(charArg as CollideArg);
        const O = getMetaFromAny(objArg as CollideArg);

        // Exiger >= 50% d'overlap (réglable via PICKABLE.OVERLAP_RATIO)
        const ratio = overlapRatioCharOverObject(charGO, objGO);
        if (ratio < PICKABLE.OVERLAP_RATIO) {
            return;
        }

        const picked = C.entity?.pickUpObject?.(O.entity) ?? false;

        if (picked) {
            const body = (objGO as any).body as Phaser.Physics.Arcade.Body | undefined;
            if (body) body.enable = false;
            (objGO as any).setVisible?.(false);
            (objGO as any).active = false;
        }

        this.colliders.log("Char↔Obj [pickable]", { char: C.id, obj: O.id, picked });
        this.events?.emit("collision:char-pick", { charId: C.id, objectId: O.id, char: C.entity, object: O.entity, picked });
        this.events?.emit("object:picked", { objectId: O.id, by: C.id, object: O.entity, char: C.entity, picked });
        this.win?.evaluateOnEvent?.({ type: "object:picked", by: C.id, objectId: O.id } as any);

        if (C.id && O.id) {
            this.events?.dispatchPickupEvent(C.id, O.id);
            this.events?.emit("collisionEvent", C.id, O.id);
        }
    };

    private onCharImmovableCollide: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (charArg, objArg) => {

        if (!isCharAny(charArg as CollideArg) || !isObjAny(objArg as CollideArg)) return;

        const charGO = asGO(charArg as CollideArg);
        if (!charGO) return;

        const C = getMetaFromAny(charArg as CollideArg);
        const O = getMetaFromAny(objArg as CollideArg);

        const bodyC = (charGO as any).body as Phaser.Physics.Arcade.Body | undefined;
        bodyC?.setVelocity(0, 0);

        C.entity?.playCollisionAnimation?.(O.entity);

        this.colliders.log("Char↔Obj [immovable]", { char: C.id, obj: O.id });
        this.events?.emit("collision:char-immovable", { charId: C.id, objectId: O.id, char: C.entity, object: O.entity });
        this.win?.evaluateOnEvent?.({ type: "collision:char-immovable", charId: C.id, objectId: O.id } as any);

        if (C.id && O.id) {
            this.events?.dispatchCharacterObjectCollisionEvent(C.id, O.id); // typed
            this.events?.emit("collisionEvent", C.id, O.id);                 // générique écouté par l’IDE 
        }
    };

    private onCharPushableCollide: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (charArg, objArg) => {
        // sécurise les types (Char/Object attendus)
        if (!isCharAny(charArg as CollideArg) || !isObjAny(objArg as CollideArg)) return;

        // normalise en GameObject
        const charGO = asGO(charArg as CollideArg);
        const objGO = asGO(objArg as CollideArg);
        if (!charGO || !objGO) return;

        // bodies Arcade
        const bodyC = (charGO as any).body as Phaser.Physics.Arcade.Body | undefined;
        const bodyO = (objGO as any).body as Phaser.Physics.Arcade.Body | undefined;
        if (!bodyC || !bodyO) return;

        // vitesse du perso
        const { x: vx, y: vy } = bodyC.velocity;
        const mag = Math.hypot(vx, vy);

        // direction de poussée
        let ux = 0, uy = 0;
        if (mag >= this.minCharSpeedToPush) {
            // cas normal : on suit la vitesse du perso
            ux = vx / mag; uy = vy / mag;
        } else {
            // perso “collé” (vitesse annulée par la séparation) → pousser dans l’axe perso→objet
            const getCenter = (go: any) =>
                go?.getCenter?.() ?? {
                    x: (go.body?.x ?? 0) + (go.body?.halfWidth ?? 0),
                    y: (go.body?.y ?? 0) + (go.body?.halfHeight ?? 0)
                };
            const cc = getCenter(charGO);
            const oc = getCenter(objGO);
            const dx = oc.x - cc.x, dy = oc.y - cc.y;
            const d = Math.hypot(dx, dy) || 1;
            ux = dx / d; uy = dy / d;

            bodyO.setMaxVelocity(this.objectMaxVelocity, this.objectMaxVelocity);
            bodyO.setDamping(false);
            bodyO.setDrag(this.objectDrag, this.objectDrag);
            bodyO.moves = true; bodyO.setImmovable(false); bodyO.enable = true;
            bodyO.setVelocity(ux * PUSHABLE.MIN_TOUCH_PUSH, uy * PUSHABLE.MIN_TOUCH_PUSH);
            return; // on sort ici pour éviter de recalculer pushSpeed
        }

        this.colliders.log("push", {
            vChar: Math.round(mag),
            allow: mag >= this.minCharSpeedToPush,
            drag: this.objectDrag,
            oMoves: bodyO.moves,
            oImmov: bodyO.immovable,
            oEnabled: bodyO.enable,
            maxV: this.objectMaxVelocity
        });

        // vitesse de poussée bornée
        const pushSpeed = Math.min(mag * this.pushScale, this.pushVelocity);

        // appliquer (défensif : s'assurer qu'il est bien "dynamic/movable")
        bodyO.setMaxVelocity(this.objectMaxVelocity, this.objectMaxVelocity);
        bodyO.setDamping(false);
        bodyO.setDrag(this.objectDrag, this.objectDrag);
        bodyO.moves = true;
        bodyO.setImmovable(false);
        bodyO.enable = true;
        bodyO.setVelocity(ux * pushSpeed, uy * pushSpeed);

        // Freinage dur en dessous d'un petit seuil
        const STOP_EPS = PUSHABLE.STOP_EPS;
        if (typeof (bodyO as any).speed === "number") {
            const speed = (bodyO as any).speed as number;
            if (speed < STOP_EPS) {
                bodyO.setVelocity(0, 0);
            }
        }

        // métadonnées sûres (id/entity/flags) via helper
        const C = getMetaFromAny(charArg as CollideArg);
        const O = getMetaFromAny(objArg as CollideArg);

        this.colliders.log("Char↔Obj [pushable]", {
            vChar: Math.round(mag),
            pushSpeed: Math.round(pushSpeed),
            cap: this.pushVelocity
        });

        this.events?.emit("collision:char-push", {
            charId: C.id,
            objectId: O.id,
            char: C.entity,
            object: O.entity,
            dir: { x: ux, y: uy }
        });

        this.win?.evaluateOnEvent?.({
            type: "collision:char-push",
            charId: C.id,
            objectId: O.id
        } as any);

        if (C.id && O.id) {
            this.events?.dispatchCharacterObjectCollisionEvent(C.id, O.id); // typed
            this.events?.emit("collisionEvent", C.id, O.id);                 // générique écouté par l’IDE
        }
    };



    private onObjObjCollide: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (aArg, bArg) => {
        if (!isObjAny(aArg as CollideArg) || !isObjAny(bArg as CollideArg)) return;

        const A = getMetaFromAny(aArg as CollideArg);
        const B = getMetaFromAny(bArg as CollideArg);

        this.colliders.log("Obj↔Obj", { A: A.id, B: B.id });
        this.events?.emit("collision:obj-obj", { aId: A.id, bId: B.id, a: A.entity, b: B.entity });
        this.win?.evaluateOnEvent?.({ type: "collision:obj-obj", aId: A.id, bId: B.id } as any);

        if (A.id && B.id) this.events?.emit("collisionEvent", A.id, B.id);
    };

    // ────────────────────── Règles “par défaut” ──────────────────────
    configureDefaultRules() {
        this.addRule(
            // Character ↔ Character
            { id: "char-char", a: "characters", b: "characters", type: "collide", onCollide: this.onCharCharCollide },

            // Character ↔ Object (pickable → overlap)
            {
                id: "char-pick", a: "characters", b: "objects", type: "overlap",
                process: this.processPickable, onCollide: this.onCharPickableOverlap
            },

            // Character ↔ Object (immovable → collide)
            {
                id: "char-immovable", a: "characters", b: "objects", type: "collide",
                process: this.processImmovable, onCollide: this.onCharImmovableCollide
            },

            // Character ↔ Object (pushable → collide)
            {
                id: "char-push", a: "characters", b: "objects", type: "overlap",
                process: this.processPushable, onCollide: this.onCharPushableCollide
            },

            // Object ↔ Object
            { id: "obj-obj", a: "objects", b: "objects", type: "collide", onCollide: this.onObjObjCollide },
        );
        return this;
    }
}
